import { useUserStore } from "~/store/global/user.store";

import { fetchPricingItemsApi, getStripeLinkApi, fetchModalData, submitCreditPackApi } from "../api";
import type { ICreditPackPayload, IPricingItem, IPricingPackage, IStripePayload } from "../types";
import type { TLocals } from "~/types/global/user.type";
export const usePricingStore = defineStore("pricing-store", () => {
    const auth = useAuth();
    const { locale } = useI18n();
    const { userData, showNoActivePlan } = storeToRefs(useUserStore());
    const { openSnackbar } = useSnackbar();
    const showOverlay = ref(false);

    const activeCycle = ref<"yearly" | "monthly">("yearly");
    const fetchingPricingItems = ref(true);

    // * credit pack
    const showCreditPackDialog = ref(false);
    const showMoreInputsDialog = ref(false);

    const showDowngradeSuccessModal = ref(false);
    const pricingCardsDataYearly = ref<IPricingItem[]>([] as IPricingItem[]);
    const pricingCardsDataMonthly = ref<IPricingItem[]>([] as IPricingItem[]);

    const showCurrencyMismatch = ref(false);

    const enterprisePricePackages = computed((): IPricingPackage[] => {
        const [enterprisePlan] = pricingCardsData.value.filter((plan) => {
            return plan.enterprise;
        });
        return enterprisePlan.price_packages as IPricingPackage[];
    });
    const initialIndexOfActivePricePackage = computed((): number => {
        return auth.value && !userData.value.free_plan && userData.value?.credit_plan_index
            ? userData.value.credit_plan_index
            : 0;
    });
    const activePricePackageIndex = ref(initialIndexOfActivePricePackage.value);
    const enterpriseItems = ref();
    const selectedPricePackage = ref<IPricingPackage>({} as IPricingPackage);

    const initialCurrency = (): string => {
        // * if user is logged in and has user credit data
        if (auth.value && userData.value.currency && !userData.value.free_plan) return userData.value.currency;

        // * set currency based on user locale
        let localsCurrency: Record<TLocals, string> = {
            en: "usd",
            fr: "eur",
            "en-gb": "gbp",
        };

        return localsCurrency[locale.value as TLocals] || "usd";
    };

    const currentCurrency = ref("");
    const fetchPricingPlansData = async (): Promise<void> => {
        fetchingPricingItems.value = true;

        try {
            const [responsePersonalItems, responseEnterpriseItems] = await Promise.all([
                fetchPricingItemsApi(currentCurrency.value, activeCycle.value, "personal"),
                fetchPricingItemsApi(currentCurrency.value, activeCycle.value, "enterprise"),
            ]);

            const combinedItems = [
                ...(responsePersonalItems.data.value as IPricingItem[]),
                ...(responseEnterpriseItems.data.value as IPricingItem[]),
            ];

            if (activeCycle.value === "yearly") {
                pricingCardsDataYearly.value = combinedItems;
            } else {
                pricingCardsDataMonthly.value = combinedItems;
            }
            selectedPricePackage.value = enterprisePricePackages.value[activePricePackageIndex.value];
        } catch (error) {
            console.error("Error fetching pricing plans data:", error);
        } finally {
            fetchingPricingItems.value = false;
        }
    };

    const fetchEnterprisePricingItems = async (currency: string, cycle: string) => {
        const { data } = await fetchPricingItemsApi(currency, cycle, "enterprise");
        if (data.value) {
            enterpriseItems.value = data.value;
        }
    };

    const pricingCardsData = computed((): IPricingItem[] => {
        return activeCycle.value === "yearly" ? pricingCardsDataYearly.value : pricingCardsDataMonthly.value;
    });

    const ratioIndex = ref(
        auth.value ? (userData.value && userData.value.credit_plan_index ? userData.value.credit_plan_index : 0) : 0
    );

    const expirationDateOnDowngrade = ref();

    const checkoutLoading = ref<boolean>(false);
    const showStripeForm = ref<boolean>(false);
    const stripeSecretKey = ref<string>("");

    const showEmbeddedStripCheckout = (clientSecret: string) => {
        stripeSecretKey.value = clientSecret;
        showStripeForm.value = true;
    };

    const getStripeLink = async (payload: IStripePayload, locale: string) => {
        const { data, error } = await getStripeLinkApi(payload, locale);
        if (data.value) {
            checkoutLoading.value = false;
            showEmbeddedStripCheckout((data.value as { client_secret: string }).client_secret);
        }
        if (error.value?.data.key === "api_error_pricing_currency_mismatch") {
            showCurrencyMismatch.value = true;
        }
    };
    const downgradeFunc = async (payload: IStripePayload, locale: string) => {
        return await getStripeLinkApi(payload, locale);
    };
    const getModalData = async (plan_id: string, currency: string, quantity?: number) => {
        return await fetchModalData(plan_id, currency, quantity);
    };

    const submitCreditPack = async (payload: ICreditPackPayload, locale: string) =>
        useLazyAsyncData(
            "submit-credit-pack",
            async () =>
                await submitCreditPackApi(payload, locale).then(({ data, error }) => {
                    if (data.value) {
                        return data.value as { client_secret: string };
                    } else {
                        // * this code is for typescript errors and stuff
                        // return null;
                        if (error.value?.statusCode === 403 && error.value?.data.key === "api_error_access_denied") {
                            showCreditPackDialog.value = false;
                            showNoActivePlan.value = true;
                            return;
                        }
                    }
                }),
            {
                immediate: false,
            }
        );

    return {
        // * states
        activeCycle,
        fetchingPricingItems,
        showOverlay,
        pricingCardsData,
        currentCurrency,
        showCurrencyMismatch,
        showCreditPackDialog,
        showMoreInputsDialog,
        checkoutLoading,
        showDowngradeSuccessModal,
        expirationDateOnDowngrade,
        ratioIndex,
        selectedPricePackage,
        pricingCardsDataYearly,
        pricingCardsDataMonthly,
        enterpriseItems,
        // * computed
        // personalItems,
        // enterpriseItems,
        // initialCurrency,
        enterprisePricePackages,
        activePricePackageIndex,
        stripeSecretKey,
        showStripeForm,
        // * methods
        getStripeLink,
        submitCreditPack,
        getModalData,
        downgradeFunc,
        fetchPricingPlansData,
        fetchEnterprisePricingItems,
        initialCurrency,
        showEmbeddedStripCheckout,
    };
});
